/**
 * Fullscreen incoming-call modal — animates in when IncomingCallContext detects
 * a fresh, unanswered "live" buddy invite. Plays a continuous ring tone via
 * Web Audio + buzzes the device (where supported) so the recipient cannot miss
 * the call even if the app is in the background tab.
 *
 * - Accept → POST /accept, then live.startSession + dismiss modal. The caller's
 *   auto-launch logic will fire once the participant count hits quorum.
 * - Decline → POST /decline + dismiss.
 */
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneOff, Video } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useIncomingCall } from '../../context/IncomingCallContext';
import { useLiveVideo } from '../../context/LiveVideoContext';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getImageUrl } from '../../utils/imageUtils';

// Two-tone ringtone played in a loop via Web Audio. Avoids shipping an MP3.
function startRingtone() {
  let ctx;
  try {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
  } catch { return () => {}; }
  let stopped = false;
  const playPulse = (freq, when, dur) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = freq;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0, when);
    gain.gain.linearRampToValueAtTime(0.18, when + 0.04);
    gain.gain.linearRampToValueAtTime(0, when + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(when);
    osc.stop(when + dur);
  };
  const loop = () => {
    if (stopped) return;
    const t = ctx.currentTime;
    playPulse(880, t,        0.30);
    playPulse(660, t + 0.32, 0.30);
    setTimeout(loop, 1500);
  };
  // Some browsers suspend AudioContext until a user gesture. Best-effort.
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  loop();
  return () => {
    stopped = true;
    try { ctx.close(); } catch { /* noop */ }
  };
}

export default function IncomingCallModal() {
  const { activeInvite, dismissInvite } = useIncomingCall();
  const { api } = useAuth();
  const live = useLiveVideo();
  const [responding, setResponding] = useState(false);
  const stopRingRef = useRef(null);

  // Start ringtone + vibration when an invite appears, stop when it goes away.
  useEffect(() => {
    if (!activeInvite) return undefined;
    stopRingRef.current = startRingtone();
    let vibrationInterval;
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate([400, 200, 400, 200, 400]);
      vibrationInterval = setInterval(() => {
        try { navigator.vibrate([400, 200, 400, 200, 400]); } catch { /* noop */ }
      }, 2000);
    }
    return () => {
      try { stopRingRef.current?.(); } catch { /* noop */ }
      stopRingRef.current = null;
      if (vibrationInterval) clearInterval(vibrationInterval);
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        try { navigator.vibrate(0); } catch { /* noop */ }
      }
    };
  }, [activeInvite?.id]);

  if (!activeInvite) return null;

  const caller = (activeInvite.participants || []).find((p) => p.user_id === activeInvite.created_by) || {};
  const callerName = caller.name || activeInvite.workout_title || 'Your buddy';

  const handleAccept = async () => {
    if (responding) return;
    setResponding(true);
    try {
      await api().post(`/buddies/workout-invite/${activeInvite.id}/accept`);
      toast.success(`Joining ${callerName}…`, { icon: '📹' });
      const r = await live.startSession(activeInvite.id);
      if (!r?.ok) toast.error(r?.error || 'Could not join the call');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept the call');
    }
    dismissInvite(activeInvite.id);
    setResponding(false);
  };

  const handleDecline = async () => {
    if (responding) return;
    setResponding(true);
    try {
      await api().post(`/buddies/workout-invite/${activeInvite.id}/decline`);
    } catch { /* silent — UX still dismisses */ }
    dismissInvite(activeInvite.id);
    setResponding(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-6"
        data-testid="incoming-call-modal"
      >
        {/* Pulsing outer ring */}
        <div className="relative flex flex-col items-center gap-6 max-w-md w-full">
          <p className="font-heading text-primary text-sm tracking-[0.3em] uppercase animate-pulse">
            Incoming Video Call
          </p>
          <div className="relative">
            <span className="absolute inset-0 rounded-full bg-primary/30 animate-ping" />
            <span className="absolute inset-[-12px] rounded-full bg-primary/20 animate-ping [animation-delay:300ms]" />
            <Avatar className="relative h-32 w-32 border-4 border-primary shadow-2xl">
              <AvatarImage src={getImageUrl(caller.profile_picture)} />
              <AvatarFallback className="bg-primary/20 text-primary text-3xl font-heading">
                {callerName.charAt(0)}
              </AvatarFallback>
            </Avatar>
          </div>

          <div className="text-center">
            <p className="text-3xl text-white font-heading uppercase tracking-wider" data-testid="incoming-call-name">
              {callerName}
            </p>
            <p className="text-muted-foreground text-sm mt-2 flex items-center justify-center gap-1.5">
              <Video className="h-3.5 w-3.5" />
              Live Workout Call
            </p>
          </div>

          <div className="flex items-center justify-center gap-12 mt-4 w-full">
            <button
              onClick={handleDecline}
              disabled={responding}
              className="flex flex-col items-center gap-2 group"
              data-testid="incoming-call-decline"
              aria-label="Decline call"
            >
              <span className="h-16 w-16 rounded-full bg-red-600 group-hover:bg-red-700 transition-colors flex items-center justify-center shadow-2xl group-active:scale-95 transition-transform">
                <PhoneOff className="h-7 w-7 text-white" />
              </span>
              <span className="text-xs text-white/70 uppercase tracking-wider">Decline</span>
            </button>
            <button
              onClick={handleAccept}
              disabled={responding}
              className="flex flex-col items-center gap-2 group"
              data-testid="incoming-call-accept"
              aria-label="Accept call"
            >
              <span className="h-16 w-16 rounded-full bg-green-600 group-hover:bg-green-700 transition-colors flex items-center justify-center shadow-2xl group-active:scale-95 transition-transform animate-pulse">
                <Phone className="h-7 w-7 text-white" />
              </span>
              <span className="text-xs text-white/70 uppercase tracking-wider">Accept</span>
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
